import { jsPDF } from 'jspdf';
import QRCode from 'qrcode';
import logoUrl from '@/assets/yggdat_logo.png';

export const CARD_SECTIONS = [
  {
    title: 'Safety',
    fields: [
      { key: 'emergency_shutdown', label: 'Emergency shutdown' },
      { key: 'safety_hazards', label: 'Safety hazards' },
      { key: 'chemicals_quantity', label: 'Chemicals and quantity' },
      { key: 'safety_rules', label: 'Safety rules' },
      { key: 'restrictions', label: 'Restrictions' },
    ],
  },
  {
    title: 'Maintenance',
    fields: [
      { key: 'maintenance', label: 'Maintenance' },
      { key: 'service_company', label: 'Service company' },
    ],
  },
  {
    title: 'Other',
    fields: [{ key: 'other_info', label: 'Other information' }],
  },
];

export const CARD_FIELDS = CARD_SECTIONS.flatMap((section) => section.fields.map((field) => field.key));

export const HIGHLIGHTED_FIELD = 'emergency_shutdown';

const PRIMARY = [24, 57, 43];
const ACCENT = [0, 0, 0];
const DANGER = [176, 32, 32];
const MUTED = [110, 110, 110];
const FRAME = [190, 190, 190];
const MARGIN = 15;
const LINE_HEIGHT = 5;
const FRAME_INSET = 6;
const LOGO_BADGE_SIZE = 28;

async function loadLogoDataUrl() {
  try {
    const blob = await (await fetch(logoUrl)).blob();
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.error('Failed to load logo for instrument card PDF:', error);
    return null;
  }
}

export async function buildInstrumentCardPdf(card, instrumentUrl) {
  const pdf = new jsPDF({ unit: 'mm', format: 'a4' });
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const contentWidth = pageWidth - MARGIN * 2;
  const bottomLimit = pageHeight - FRAME_INSET - 5 - LOGO_BADGE_SIZE - 4;
  let y = MARGIN;
  const logoDataUrl = await loadLogoDataUrl();
  const qrDataUrl = await QRCode.toDataURL(instrumentUrl, { width: 480, margin: 1 });

  const ensureSpace = (height) => {
    if (y + height > bottomLimit) {
      pdf.addPage();
      y = MARGIN;
    }
  };

  // Header with logo
  const qrSize = 36;
  const headerBadgeX = pageWidth - MARGIN - qrSize;
  if (logoDataUrl) {
    const logoPadding = 3;
    pdf.setFillColor(...PRIMARY);
    pdf.roundedRect(headerBadgeX, y, qrSize, qrSize, 2, 2, 'F');
    pdf.addImage(
      logoDataUrl,
      'PNG',
      headerBadgeX + logoPadding,
      y + logoPadding,
      qrSize - logoPadding * 2,
      qrSize - logoPadding * 2
    );
  }

  const headerWidth = contentWidth - qrSize - 8;
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(10);
  pdf.setTextColor(...ACCENT);
  pdf.text('INSTRUMENT CARD', MARGIN, y + 4);

  pdf.setFontSize(20);
  pdf.setTextColor(0, 0, 0);
  const titleLines = pdf.splitTextToSize(card.name || 'Instrument', headerWidth);
  pdf.text(titleLines, MARGIN, y + 13);
  let headerY = y + 13 + titleLines.length * 8;

  pdf.setFontSize(10);
  [
    ['ID', card.serial],
    ['Location', card.location_name],
    ['Responsible user', card.user_name],
  ].forEach(([label, value]) => {
    pdf.setFont('helvetica', 'bold');
    pdf.text(`${label}:`, MARGIN, headerY);
    pdf.setFont('helvetica', 'normal');
    pdf.text(pdf.splitTextToSize(value || 'N/A', headerWidth - 35)[0], MARGIN + 35, headerY);
    headerY += LINE_HEIGHT + 1;
  });

  y = Math.max(headerY, y + qrSize + 8) + 2;
  y += 4;

  // Emergency shutdown gets a prominent box above the regular sections
  const shutdown = card[HIGHLIGHTED_FIELD];
  if (shutdown) {
    pdf.setFontSize(11);
    const lines = pdf.splitTextToSize(shutdown, contentWidth - 10);
    const boxHeight = 12 + lines.length * LINE_HEIGHT;
    ensureSpace(boxHeight);
    pdf.setDrawColor(...DANGER);
    pdf.setLineWidth(0.8);
    pdf.roundedRect(MARGIN, y, contentWidth, boxHeight, 2, 2, 'S');
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(...DANGER);
    pdf.text('EMERGENCY SHUTDOWN', MARGIN + 5, y + 7);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(0, 0, 0);
    pdf.text(lines, MARGIN + 5, y + 13);
    y += boxHeight + 8;
  }

  CARD_SECTIONS.forEach((section) => {
    const fields = section.fields.filter((field) => field.key !== HIGHLIGHTED_FIELD);
    if (!fields.length) return;

    ensureSpace(16);
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(13);
    pdf.setTextColor(...ACCENT);
    pdf.text(section.title, MARGIN, y);
    pdf.setDrawColor(...ACCENT);
    pdf.setLineWidth(0.6);
    pdf.line(MARGIN, y + 2, pageWidth - MARGIN, y + 2);
    y += 8;

    fields.forEach(({ key, label }) => {
      pdf.setFontSize(10);
      const lines = pdf.splitTextToSize(card[key] || 'Not specified', contentWidth);
      ensureSpace(LINE_HEIGHT * (lines.length + 1) + 3);

      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(0, 0, 0);
      pdf.text(label, MARGIN, y);
      y += LINE_HEIGHT;

      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(...(card[key] ? [0, 0, 0] : MUTED));
      lines.forEach((line) => {
        ensureSpace(LINE_HEIGHT);
        pdf.text(line, MARGIN, y);
        y += LINE_HEIGHT;
      });
      y += 3;
    });
    y += 4;
  });

  const pageCount = pdf.getNumberOfPages();
  const generated = `Generated ${new Date().toLocaleString()}`;
  const badgeX = pageWidth - MARGIN - LOGO_BADGE_SIZE;
  const badgeY = pageHeight - FRAME_INSET - 5 - LOGO_BADGE_SIZE;
  const footerWidth = badgeX - MARGIN - 4;

  for (let page = 1; page <= pageCount; page += 1) {
    pdf.setPage(page);

    pdf.setDrawColor(...FRAME);
    pdf.setLineWidth(0.8);
    pdf.roundedRect(FRAME_INSET, FRAME_INSET, pageWidth - FRAME_INSET * 2, pageHeight - FRAME_INSET * 2, 3, 3, 'S');

    pdf.setFillColor(255, 255, 255);
    pdf.roundedRect(badgeX, badgeY, LOGO_BADGE_SIZE, LOGO_BADGE_SIZE, 2, 2, 'F');
    pdf.addImage(qrDataUrl, 'PNG', badgeX, badgeY, LOGO_BADGE_SIZE, LOGO_BADGE_SIZE);
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(7);
    pdf.setTextColor(...MUTED);
    pdf.text('Scan to open', badgeX + LOGO_BADGE_SIZE / 2, badgeY + LOGO_BADGE_SIZE + 3, { align: 'center' });

    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(8);
    pdf.setTextColor(...MUTED);
    pdf.text(pdf.splitTextToSize(`${generated}  |  ${instrumentUrl}`, footerWidth)[0], MARGIN, badgeY + LOGO_BADGE_SIZE - 5);
    pdf.text(`Page ${page} of ${pageCount}`, MARGIN, badgeY + LOGO_BADGE_SIZE - 1);
  }

  return pdf.output('blob');
}
